import calculateMinutesDuration from 'clientState/calculateMinutesDuration';
import sectionResolver from './section';
import {
  Carrier,
  SectionFragment,
  SectionLocationFragment,
  SegmentFragment,
  TransportType,
} from 'generated/graphql';

export type ResolvedSegment = ReturnType<typeof segmentResolver>;

export type LocationType = 'airport' | 'station' | 'terminal' | 'none';
const nodeTypes: Record<TransportType, LocationType> = {
  PLANE: 'airport',
  TRAIN: 'station',
};

const calculateLocationChangeType = (
  destination: SectionLocationFragment,
  departure: SectionLocationFragment,
  arrivalTerminal?: string | null,
  departureTerminal?: string | null,
  transportType?: TransportType | null
): LocationType => {
  if (destination.id !== departure.id) {
    return nodeTypes[transportType as TransportType];
  } else if (arrivalTerminal !== departureTerminal) {
    return 'terminal';
  }
  return 'none';
};

const hasChangeOfCountry = (previousSection, nextSection) => {
  return (
    previousSection.departure.countryName !== nextSection.departure.countryName
  );
};

const calculateTransportTypeChange = (
  previousTransportType,
  nextTransportType
) => previousTransportType !== nextTransportType;

const calculateSelfTransfer = (
  transportTypeChange: boolean,
  locationChangeType: LocationType
): boolean =>
  transportTypeChange ||
  locationChangeType === 'airport' ||
  locationChangeType === 'station';

const createStopover = (
  previousSection: SectionFragment,
  nextSection: SectionFragment
) => {
  const {
    destination,
    arrivalDate,
    arrivalTerminal,
    carrier,
    insuranceOffer,
    transportType: previousTransportType,
  } = previousSection;
  const { departure, departureTerminal, departureDate, transportType } =
    nextSection;
  const transportTypeChange = calculateTransportTypeChange(
    previousTransportType,
    transportType
  );
  const locationChangeType = calculateLocationChangeType(
    destination,
    departure,
    arrivalTerminal,
    departureTerminal,
    transportType
  );
  return {
    duration: calculateMinutesDuration(departureDate, arrivalDate),
    transportType,
    transportTypeChange,
    locationChangeType,
    carrierChange: carrier.id !== nextSection.carrier.id,
    insuranceOffer: insuranceOffer || {
      policy: 'CARRIER_GUARANTEE',
      carrier,
    },
    vinRestriction: {
      enabled: !!(
        hasChangeOfCountry(previousSection, nextSection) &&
        insuranceOffer &&
        insuranceOffer.policy
      ),
      country: departure.countryName,
    },
    selfTransfer: calculateSelfTransfer(
      transportTypeChange,
      locationChangeType
    ),
    stopoverLocations: {
      origin: previousSection.departure.countryName,
      destination: previousSection.destination.countryName,
      isComingFromAnotherCountry:
        previousSection.departure.countryName !==
        previousSection.destination.countryName,
    },
    changeCity: destination.cityName,
  };
};

const MAX_DISTINCT_CARRIERS = 3;

export default function segmentResolver(segment: SegmentFragment) {
  const { sections } = segment;
  const stops = sections.slice(0, -1).map((previousSection, index) => {
    const nextSection = sections[index + 1];
    return createStopover(previousSection, nextSection);
  });
  const carriers: Carrier[] = Array.from(
    sections
      .reduce((carriers, { carrier }) => {
        return carriers.set(carrier.id, carrier);
      }, new Map())
      .values()
  ).slice(0, MAX_DISTINCT_CARRIERS);
  const firstSection = sections[0];
  const lastSection = sections[sections.length - 1];
  return {
    ...segment,
    carriers,
    departureDate: firstSection.departureDate,
    arrivalDate: lastSection.arrivalDate,
    departure: firstSection.departure,
    destination: lastSection.destination,
    sections: sections.map((section, idx) =>
      sectionResolver(section, idx === sections.length - 1, idx === 0)
    ),
    stops,
  };
}
